import React from 'react';
import { Icon, Table } from 'semantic-ui-react';

import { FormatCurrency } from '../../../util';
import {
  StandaloneChargeCommissionPaymentDTO,
  StandaloneChargeDetailDTO
} from '../../../services/standalone-charges';

type Props = {
  detail: StandaloneChargeDetailDTO;
};

const money = (value: number) => `R$ ${FormatCurrency(Number(value.toFixed(2)))}`;

const percent = (value: number) =>
  `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2
  })}%`;

const asNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const collaboratorName = (
  payment: StandaloneChargeCommissionPaymentDTO,
  index: number
): string =>
  payment.collaboratorName ||
  payment.recipientName ||
  payment.user?.name ||
  payment.collaborator?.user?.name ||
  `Colaborador ${index + 1}`;

const ChargeDistributionSummary: React.FC<Props> = ({ detail }) => {
  const amountInCents = Math.round(asNumber(detail.amount) * 100);
  const hasPlatformPercent =
    detail.platformSplitPercent !== undefined && detail.platformSplitPercent !== null;
  const hasEstatePercent =
    detail.estateSplitPercent !== undefined && detail.estateSplitPercent !== null;
  const platformPercent = Math.max(
    hasPlatformPercent
      ? asNumber(detail.platformSplitPercent)
      : hasEstatePercent
      ? 100 - asNumber(detail.estateSplitPercent)
      : 0,
    0
  );
  const estatePercent =
    hasEstatePercent
      ? Math.max(asNumber(detail.estateSplitPercent), 0)
      : Math.max(100 - platformPercent, 0);
  const estateGrossInCents = Math.floor((amountInCents * estatePercent) / 100);
  const operationFeeInCents = Math.max(amountInCents - estateGrossInCents, 0);
  const payments = detail.commissionPayments || [];
  const commissionTotalInCents = payments.reduce(
    (total, payment) => total + Math.round(asNumber(payment.commissionAmount) * 100),
    0
  );
  const estateNetInCents = Math.max(estateGrossInCents - commissionTotalInCents, 0);
  const collaboratorTotalPercent = payments.reduce(
    (total, payment) => total + asNumber(payment.commissionPercent),
    0
  );
  const estateDistributionPercent = Math.max(100 - collaboratorTotalPercent, 0);
  const hasCollaborators = payments.length > 0;
  const sameCommissionPercent = payments.every(
    payment =>
      asNumber(payment.commissionPercent) === asNumber(payments[0]?.commissionPercent)
  );

  return (
    <section style={{ marginBottom: 28 }}>
      <h2
        style={{
          color: '#5B21B6',
          borderBottom: '1px solid #D8B4FE',
          fontSize: 16,
          paddingBottom: 8,
          margin: '0 0 12px'
        }}
      >
        Resumo da cobrança
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
          marginBottom: 12
        }}
      >
        <div style={{ border: '1px solid #DDDDDD', borderRadius: 6, padding: '12px 14px' }}>
          <div style={{ color: '#767676', fontSize: 12, marginBottom: 8 }}>
            Valor da cobrança
          </div>
          <strong style={{ color: '#2B313C', fontSize: 16 }}>
            {money(amountInCents / 100)}
          </strong>
        </div>
        <div style={{ border: '1px solid #DDDDDD', borderRadius: 6, padding: '12px 14px' }}>
          <div style={{ color: '#767676', fontSize: 12, marginBottom: 8 }}>
            Taxa de operação
          </div>
          <strong style={{ color: '#2B313C', fontSize: 16 }}>
            {percent(platformPercent)}
          </strong>{' '}
          <span style={{ color: '#2B313C', fontSize: 13 }}>
            ({money(operationFeeInCents / 100)})
          </span>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #DDDDDD',
          borderRadius: 6,
          padding: '12px 14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12
        }}
      >
        <span style={{ color: '#767676', fontSize: 13 }}>Valor líquido a distribuir</span>
        <strong style={{ color: '#2B313C', fontSize: 16 }}>
          {money(estateGrossInCents / 100)}
        </strong>
      </div>

      {hasCollaborators && (
        <div
          style={{
            border: '1px solid #DDDDDD',
            borderRadius: 6,
            padding: '12px 14px',
            marginBottom: 12
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 6,
              color: '#767676',
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              marginBottom: 8
            }}
          >
            <span>Divisão do valor líquido</span>
            <span>
              {payments.length} {payments.length === 1 ? 'colaborador' : 'colaboradores'}
              {sameCommissionPercent && (
                <span style={{ color: '#7D45E8' }}>
                  {' '}- {percent(asNumber(payments[0].commissionPercent))} cada
                </span>
              )}
            </span>
          </div>
          <div
            style={{
              display: 'flex',
              height: 8,
              borderRadius: 4,
              overflow: 'hidden',
              background: '#E5E7EB',
              marginBottom: 8
            }}
          >
            <div style={{ width: `${estateDistributionPercent}%`, background: '#6B7280' }} />
            <div style={{ width: `${collaboratorTotalPercent}%`, background: '#8B5CF6' }} />
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              color: '#767676',
              fontSize: 12
            }}
          >
            <span>
              <Icon name="circle" style={{ color: '#6B7280', fontSize: 8 }} />
              Imobiliária{' '}
              <strong style={{ color: '#6B7280' }}>
                {percent(estateDistributionPercent)}
              </strong>
            </span>
            <span>
              <Icon name="circle" style={{ color: '#8B5CF6', fontSize: 8 }} />
              Colaboradores{' '}
              <strong style={{ color: '#8B5CF6' }}>
                {percent(collaboratorTotalPercent)}
              </strong>
            </span>
          </div>
        </div>
      )}

      <Table celled compact="very" style={{ borderRadius: 6, overflow: 'hidden' }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell style={{ color: '#767676', fontSize: 12 }}>
              DESTINATÁRIO
            </Table.HeaderCell>
            {hasCollaborators && (
              <Table.HeaderCell textAlign="center" style={{ color: '#767676', width: 100 }}>
                %
              </Table.HeaderCell>
            )}
            <Table.HeaderCell textAlign="right" style={{ color: '#767676', width: 140 }}>
              VALOR
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          <Table.Row>
            <Table.Cell>{detail.estate?.name || 'Imobiliária'}</Table.Cell>
            {hasCollaborators && (
              <Table.Cell textAlign="center">
                <span
                  style={{
                    background: '#EEF0F3',
                    borderRadius: 4,
                    padding: '3px 6px',
                    color: '#6B7280'
                  }}
                >
                  {percent(estateDistributionPercent)}
                </span>
              </Table.Cell>
            )}
            <Table.Cell textAlign="right">{money(estateNetInCents / 100)}</Table.Cell>
          </Table.Row>
          {payments.map((payment, index) => (
            <Table.Row key={payment.id || payment.collaboratorId || index}>
              <Table.Cell>{collaboratorName(payment, index)}</Table.Cell>
              <Table.Cell textAlign="center">
                <span
                  style={{
                    background: '#EDE7FF',
                    borderRadius: 4,
                    padding: '3px 6px',
                    color: '#7D45E8'
                  }}
                >
                  {percent(asNumber(payment.commissionPercent))}
                </span>
              </Table.Cell>
              <Table.Cell textAlign="right">
                {money(asNumber(payment.commissionAmount))}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.HeaderCell colSpan={hasCollaborators ? 2 : 1}>
              Total repassado
            </Table.HeaderCell>
            <Table.HeaderCell textAlign="right">
              {money(estateGrossInCents / 100)}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
    </section>
  );
};

export default ChargeDistributionSummary;
